/**
 * Parses RFC 4180 CSV text into rows of trimmed cells.
 *
 * Every CSV blob this app reads is produced by `XLSX.utils.sheet_to_csv()` — either here, right
 * after `XLSX.read()` on an upload, or on the API before the file is stored. That output is always
 * comma-delimited and wraps any cell containing a comma, a quote or a newline in double quotes, so
 * splitting a line on `,` tears such a cell in two and shifts every column after it to the right
 * (PREC-3867: an exhibitor list whose company names ended in ", Ltd" showed an empty Website for
 * exactly those rows). Quotes have to be honoured, which means scanning the text rather than
 * splitting it.
 *
 * Scanning the whole text, not line by line, for the same reason: a quoted cell may contain a
 * newline (a multi-line address), and splitting on newlines first shreds that row.
 *
 * A row whose cells are all empty is dropped — `sheet_to_csv` renders a blank sheet row as `,,,`,
 * and counting those as data rows inflates the row statistics the mapping preview shows. The API's
 * `removeEmptyRows` drops them before storing, so this keeps both sides in agreement.
 */
export const parseCsvText = (text?: string): string[][] => {
	const content = text ?? "";
	const rows: string[][] = [];

	let row: string[] = [];
	let cell = "";
	let inQuotes = false;

	const endCell = (): void => {
		row.push(cell.trim());
		cell = "";
	};

	const endRow = (): void => {
		endCell();

		if (row.some((value) => value !== "")) {
			rows.push(row);
		}

		row = [];
	};

	for (let index = 0; index < content.length; index += 1) {
		const char = content[index];

		if (inQuotes) {
			if (char !== '"') {
				cell += char;
			} else if (content[index + 1] === '"') {
				// An escaped quote ("") inside a quoted cell.
				cell += '"';
				index += 1;
			} else {
				inQuotes = false;
			}

			continue;
		}

		if (char === '"') {
			inQuotes = true;
		} else if (char === ",") {
			endCell();
		} else if (char === "\n") {
			endRow();
		} else if (char === "\r") {
			if (content[index + 1] === "\n") {
				index += 1;
			}

			endRow();
		} else {
			cell += char;
		}
	}

	if (cell !== "" || row.length > 0) {
		endRow();
	}

	return rows;
};

/** The header row and the data rows of a CSV blob, in file order. */
export const parseCsvTable = (text?: string): { headers: string[]; rows: string[][] } => {
	const [headers, ...rows] = parseCsvText(text);

	return { headers: headers ?? [], rows };
};
